import React, { useEffect, useState } from "react";
import { Package, Save, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * ShippingSettingsPanel — Admin → Shop → Shipping settings.
 *
 * CMS-backed toggles that shape the shop shipping experience:
 *   • shipping.free_over            — subtotal threshold ($) for free standard
 *                                     shipping in the flat-rule fallback
 *   • shipstation.from_postal_code  — origin ZIP for real-rate carrier quotes
 *
 * Both values propagate to POST /api/shipstation/quote AND POST /api/orders
 * (create_order flat-rule fallback), so an admin can flip them for a promo
 * week without a code change.
 */
const KEYS = [
    {
        key: "shipping.free_enabled",
        label: "Free shipping enabled",
        helper: "Master switch for the free-over-$X feature. Turn OFF to charge the flat rate on every order regardless of subtotal.",
        type: "boolean",
    },
    {
        key: "shipping.free_over",
        label: "Free shipping over ($)",
        helper: "Subtotal threshold that unlocks free standard shipping in the flat-rule fallback. E.g. set to 50 during a membership drive. Ignored when the toggle above is OFF.",
        type: "number",
        step: "1",
        min: "0",
        placeholder: "75",
        prefix: "$",
        dependsOn: "shipping.free_enabled",
    },
    {
        key: "shipstation.from_postal_code",
        label: "Ship-from ZIP",
        helper: "Origin postal code used when quoting real carrier rates via ShipStation. Set to the ZIP where you actually pack orders.",
        type: "text",
        maxLength: 15,
        placeholder: "08865",
    },
];

const ShippingSettingsPanel = () => {
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState({});
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/content");
            // The /content endpoint returns a dict {key: value}, not an array.
            const initial = {};
            KEYS.forEach((k) => { initial[k.key] = data?.[k.key] ?? k.placeholder; });
            setValues(initial);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load shipping settings");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (key) => {
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.patch("/admin/content", { key, value: String(values[key] ?? "") });
            toast.success("Saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    };

    const reset = async (key) => {
        if (!window.confirm("Reset to default?")) return;
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            const { data } = await api.delete(`/admin/content/${key}`);
            setValues((v) => ({ ...v, [key]: data.value }));
            toast.success("Reset to default");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-[#4A5568] py-8" data-testid="shipping-settings-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading shipping settings…
            </div>
        );
    }

    return (
        <div data-testid="admin-shipping-settings-panel" className="space-y-6">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4F1EA] flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-[#1C252E]" strokeWidth={1.75} />
                </div>
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Shipping settings</h2>
                    <p className="text-xs text-[#4A5568] mt-1 max-w-2xl">
                        Small CMS toggles that shape the shop shipping experience. Changes take effect immediately — no redeploy needed. Both values propagate to the storefront rate quote and to the fallback shipping cost on order creation.
                    </p>
                </div>
            </div>

            <div className="grid gap-4">
                {KEYS.map((k) => {
                    // Boolean toggle — same value semantics as text but rendered as a switch.
                    if (k.type === "boolean") {
                        const isOn = String(values[k.key] ?? "true").toLowerCase() !== "false";
                        const toggle = async () => {
                            const nextVal = isOn ? "false" : "true";
                            setValues((v) => ({ ...v, [k.key]: nextVal }));
                            setSaving((s) => ({ ...s, [k.key]: true }));
                            try {
                                await api.patch("/admin/content", { key: k.key, value: nextVal });
                                toast.success(nextVal === "true" ? "Free shipping enabled" : "Free shipping disabled");
                            } catch (err) {
                                toast.error(err.response?.data?.detail || "Save failed");
                                setValues((v) => ({ ...v, [k.key]: isOn ? "true" : "false" }));
                            } finally {
                                setSaving((s) => ({ ...s, [k.key]: false }));
                            }
                        };
                        return (
                            <div key={k.key} className="bg-white border border-black/5 rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid={`shipping-setting-${k.key}`}>
                                <div className="flex-1 min-w-[240px]">
                                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                                        <label className="text-sm font-medium text-[#1C252E]">{k.label}</label>
                                        <code className="text-[10px] text-[#4A5568]">{k.key}</code>
                                    </div>
                                    <p className="text-xs text-[#4A5568] leading-relaxed">{k.helper}</p>
                                </div>
                                <button
                                    onClick={toggle}
                                    disabled={saving[k.key]}
                                    role="switch"
                                    aria-checked={isOn}
                                    data-testid={`shipping-toggle-${k.key}`}
                                    className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors ${isOn ? "bg-[#E53935]" : "bg-slate-300"} disabled:opacity-50`}
                                >
                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${isOn ? "translate-x-8" : "translate-x-1"}`} />
                                    <span className="sr-only">{isOn ? "On" : "Off"}</span>
                                </button>
                            </div>
                        );
                    }
                    // Guarded input — grays out when its `dependsOn` toggle is OFF.
                    const gated = k.dependsOn && String(values[k.dependsOn] ?? "true").toLowerCase() === "false";
                    return (
                    <div
                        key={k.key}
                        className={`bg-white border border-black/5 rounded-2xl p-6 transition-opacity ${gated ? "opacity-50" : ""}`}
                        data-testid={`shipping-setting-${k.key}`}
                    >
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                            <label htmlFor={`ship-${k.key}`} className="text-sm font-medium text-[#1C252E]">{k.label}</label>
                            <code className="text-[10px] text-[#4A5568]">{k.key}</code>
                        </div>
                        <p className="text-xs text-[#4A5568] mb-3 leading-relaxed">
                            {k.helper}
                            {gated && <span className="ml-1 text-red-700 font-medium">(disabled — toggle above is OFF)</span>}
                        </p>
                        <div className="flex items-center gap-2">
                            {k.prefix && <span className="text-[#4A5568]">{k.prefix}</span>}
                            <input
                                id={`ship-${k.key}`}
                                type={k.type}
                                step={k.step}
                                min={k.min}
                                maxLength={k.maxLength}
                                placeholder={k.placeholder}
                                disabled={gated}
                                value={values[k.key] ?? ""}
                                onChange={(e) => setValues((v) => ({ ...v, [k.key]: e.target.value }))}
                                data-testid={`shipping-input-${k.key}`}
                                className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] disabled:bg-slate-100"
                            />
                            <button
                                onClick={() => save(k.key)}
                                disabled={saving[k.key] || gated}
                                data-testid={`shipping-save-${k.key}`}
                                className="inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
                            >
                                {saving[k.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={2} />}
                                Save
                            </button>
                            <button
                                onClick={() => reset(k.key)}
                                disabled={saving[k.key] || gated}
                                data-testid={`shipping-reset-${k.key}`}
                                title="Reset to default"
                                aria-label="Reset to default"
                                className="p-2 rounded-full border border-black/10 text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-60"
                            >
                                <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ShippingSettingsPanel;
